import { MAX_BUILD } from '@platform/schema/curriculum';
import { isTerminal } from '@platform/schema/generation';

import { PUBLISH_LABEL } from '../authoring/PublishDialog';

import type {
  BuildPreflight,
  BuildStep,
  PlannedLesson,
  PlannedLessonStatus,
} from '@platform/schema/curriculum';
import type { GenerationDetail } from '@platform/schema/generation';

export type RowAction =
  | 'build'
  | 'retry'
  | 'cancel'
  | 'publish'
  | 'unpublish'
  | 'rebuild'
  | 'try'
  | 'open';

export const STATUS_LABEL: Record<PlannedLessonStatus, string> = {
  NOT_BUILT: 'Not built',
  QUEUED: 'Queued',
  BUILDING: 'Building',
  BUILT_DRAFT: 'Built - draft',
  IN_REVIEW: 'In review',
  PUBLISHED: 'Published',
  BUILD_FAILED: 'Build failed',
};

const BUILD_STEP_LABEL: Record<BuildStep, string> = {
  CHECKING_STORY: 'Checking story',
  BUILDING_SCENES: 'Building scenes',
  CHECKING_CONTINUITY: 'Checking continuity',
  DRAFT_READY: 'Draft ready',
};

export const ACTION_LABEL: Record<RowAction | DrawerAction, string> = {
  build: 'Build',
  retry: 'Retry',
  cancel: 'Cancel build',
  ...PUBLISH_LABEL,
  rebuild: 'Rebuild lesson',
  reviewStory: 'Mark reviewed',
  try: 'Try lesson',
  open: 'Open in Lessons',
  edit: 'Edit built lesson',
  source: 'Open source',
  remove: 'Remove from curriculum',
};

const BUILT: readonly RowAction[] = ['rebuild', 'try', 'open'];
const REBUILD_BLOCKS: readonly RowAction[] = [
  'publish',
  'unpublish',
  'rebuild',
];

/**
 * What a row offers in each state — the milestone's quick-actions answer and CURR-22's
 * locked proposal. In review is a draft already, so it has no Move to draft: its way
 * back is the review status on the lesson's page. A running build keeps Rebuild lesson,
 * disabled, so the row says why it cannot be pressed twice.
 */
const ACTIONS: Record<PlannedLessonStatus, readonly RowAction[]> = {
  NOT_BUILT: ['build'],
  BUILD_FAILED: ['retry'],
  QUEUED: ['cancel', 'build', 'rebuild'],
  BUILDING: ['build', 'rebuild'],
  BUILT_DRAFT: ['publish', ...BUILT],
  IN_REVIEW: ['publish', ...BUILT],
  PUBLISHED: ['unpublish', ...BUILT],
};

export const isRunning = (status: PlannedLessonStatus): boolean =>
  status === 'QUEUED' || status === 'BUILDING';

export const placeOf = (
  row: Pick<PlannedLesson, 'course' | 'module'>,
): string => `${row.course} › ${row.module}`;

/** A rebuild runs through the same statuses as a first build, so its row says which. */
export const statusLabel = ({
  status,
  rebuilding,
  buildStep,
}: Pick<PlannedLesson, 'status' | 'rebuilding' | 'buildStep'>): string => {
  if (rebuilding) return REBUILD_LABEL.rebuilding;
  return buildStep === null
    ? STATUS_LABEL[status]
    : BUILD_STEP_LABEL[buildStep];
};

/** Rebuild, Try and Open in Lessons need a lesson, whatever the status says. */
export const actionsFor = ({
  status,
  builtLessonId,
  rebuilding,
}: Pick<PlannedLesson, 'status' | 'builtLessonId' | 'rebuilding'>): {
  kind: RowAction;
  disabled: boolean;
}[] =>
  ACTIONS[status]
    .filter((kind) => builtLessonId !== null || !BUILT.includes(kind))
    .map((kind) => ({
      kind,
      disabled:
        ((kind === 'build' || kind === 'rebuild') && isRunning(status)) ||
        // Publishing mid-rebuild fails it: the API replaces only a draft.
        (rebuilding && REBUILD_BLOCKS.includes(kind)),
    }));

/**
 * The lessons one build takes from a selection: those the preflight did not set aside,
 * in the order they were selected, up to the limit. The rest are another batch.
 */
export function batchOf(
  ids: readonly string[],
  preflight: Pick<BuildPreflight, 'unknown'> & {
    unavailable: readonly { lessonId: string }[];
    blocked: readonly { lessonId: string }[];
  },
): string[] {
  const skip = new Set([
    ...preflight.unknown,
    ...preflight.unavailable.map((one) => one.lessonId),
    ...preflight.blocked.map((one) => one.lessonId),
  ]);
  return ids.filter((id) => !skip.has(id)).slice(0, MAX_BUILD);
}

export type DrawerAction =
  | 'build'
  | 'retry'
  | 'rebuild'
  | 'reviewStory'
  | 'try'
  | 'open'
  | 'edit'
  | 'source'
  | 'remove';

/** What the row and the drawer say while a built lesson's module story has moved on. */
export const STORY_CHANGED = 'Story changed — review';

const NOTHING_BUILT = 'Nothing is built yet.';

/**
 * Every action the details drawer offers, each with why it is off, or null. They are
 * all shown so an admin can see what a lesson will allow once it is built.
 */
export function drawerActions(
  row: Pick<
    PlannedLesson,
    | 'status'
    | 'builtLessonId'
    | 'primarySourceUrl'
    | 'rebuilding'
    | 'storyChanged'
  >,
): { kind: DrawerAction; disabledReason: string | null }[] {
  const built = row.builtLessonId === null ? NOTHING_BUILT : null;
  const running =
    isRunning(row.status) || row.rebuilding
      ? 'It is queued or building now.'
      : null;
  const build =
    row.status === 'NOT_BUILT'
      ? null
      : (running ??
        (row.status === 'BUILD_FAILED'
          ? 'Its build failed. Retry it instead.'
          : 'It is built already.'));
  return [
    { kind: 'build', disabledReason: build },
    {
      kind: 'retry',
      disabledReason:
        row.status === 'BUILD_FAILED'
          ? null
          : 'Only a failed build can be retried.',
    },
    { kind: 'rebuild', disabledReason: built ?? running },
    {
      kind: 'reviewStory',
      disabledReason:
        built ??
        (row.storyChanged
          ? null
          : 'Its module story has not changed since it was built or reviewed.'),
    },
    { kind: 'try', disabledReason: built },
    { kind: 'open', disabledReason: built },
    { kind: 'edit', disabledReason: built },
    {
      kind: 'source',
      disabledReason:
        row.primarySourceUrl === ''
          ? 'It has no primary source address.'
          : null,
    },
    { kind: 'remove', disabledReason: null },
  ];
}

export type RebuildPhase = 'rebuilding' | 'qa' | 'done' | 'failed';

export const REBUILD_LABEL: Record<RebuildPhase, string> = {
  rebuilding: 'Rebuilding',
  qa: 'QA',
  done: 'Rebuilt',
  failed: 'Rebuild failed',
};

/**
 * Where a rebuild is, from its job. QA is the review the rebuilt lesson must pass before
 * it replaces the old one; anything short of success leaves the lesson as it was.
 */
export function rebuildPhase(
  job: Pick<GenerationDetail, 'status'> & {
    current?: Pick<NonNullable<GenerationDetail['current']>, 'stage'>;
  },
): RebuildPhase {
  if (job.status === 'SUCCEEDED') return 'done';
  if (isTerminal(job.status)) return 'failed';
  return job.status === 'REVIEWING' || job.current?.stage === 'REVIEWING'
    ? 'qa'
    : 'rebuilding';
}
